import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../css/OrderManagement.css";
import {apiFetchPageOrder} from "../service/api/orders";

const STATUS_CONFIG = {
    1:  { label: "Pending",    bg: "#fff5ee", border: "rgba(245,158,11,0.2)",  color: "#f59e0b" },
    2:  { label: "Accepted",   bg: "#eff6ff", border: "rgba(59,130,246,0.2)",  color: "#3b82f6" },
    3:  { label: "Preparing",  bg: "#fefce8", border: "rgba(234,179,8,0.2)",   color: "#eab308" },
    4:  { label: "On the Way", bg: "#eff6ff", border: "rgba(59,130,246,0.2)",  color: "#3b82f6" },
    5:  { label: "Delivered",  bg: "#ecfdf5", border: "rgba(16,185,129,0.2)",  color: "#10b981" },
    6:  { label: "Cancelled",  bg: "#fef2f2", border: "rgba(239,68,68,0.2)",   color: "#ef4444" },
    7:  { label: "Cancelled by Store", bg: "#fdf4ff", border: "rgba(168,85,247,0.2)",  color: "#a855f7" },
    10: { label: "Picked Up",  bg: "#fff7ed", border: "rgba(249,115,22,0.2)",  color: "#f97316" },
};


const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);
const ExportIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);
const ResetIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
    </svg>
);
const ChevronLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"/>
    </svg>
);
const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);
const EyeIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
);

const StatusBadge = ({ statusId, user, delivery }) => {
    const cfg = STATUS_CONFIG[statusId] || { label: `#${statusId}`, bg: "#f1f5f9", border: "#e2e8f0", color: "#64748b" };
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="om-status-badge" style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, display: "inline-block", flexShrink: 0 }} />
                {cfg.label}
            </span>
            {user && (
                <span style={{ fontSize: 12, color: "#000", fontFamily: "'Inter', sans-serif" }}>
                    <span style={{ fontWeight: 500, color: "#64748b" }}>By</span> {user}
                </span>
            )}
            {delivery && (
                <span style={{ fontSize: 12, color: "#000", fontFamily: "'Inter', sans-serif" }}>
                    <span style={{ fontWeight: 500, color: "#6366f1" }}>Delivery</span> {delivery}
                </span>
            )}
        </div>
    );
};

const fmtDate = (str) => {
    if (!str) return "—";
    return new Date(str).toLocaleString("en-IN", {
        day: "2-digit", month: "short",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
};


const SkeletonRow = () => (
    <tr>
        {[130, 90, 180, 55, 65, 130, 150, 55].map((w, i) => (
            <td key={i} className="om-skeleton-td">
                <div className="skeleton" style={{ width: w, height: 14, borderRadius: 6 }} />
            </td>
        ))}
    </tr>
);

const PageBtn = ({ n, current, onClick }) => (
    <button className={`om-page-btn ${n === current ? "om-page-btn--active" : ""}`} onClick={() => onClick(n)}>
        {n}
    </button>
);

// ── Main ──────────────────────────────────────────────────────────────────────
const OrderManagement = () => {
    const navigate = useNavigate();
    const [orders, setOrders]             = useState([]);
    const [loading, setLoading]           = useState(true);
    const [search, setSearch]             = useState("");
    const [debouncedSearch, setDebounced] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [currentPage, setCurrentPage]   = useState(1);
    const [totalPages, setTotalPages]     = useState(1);
    const [totalCount, setTotalCount]     = useState(0);
    const [perPage, setPerPage]           = useState(15);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => { setCurrentPage(1); }, [debouncedSearch, statusFilter, perPage]);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: currentPage, per_page: perPage,
                ...(debouncedSearch && { search: debouncedSearch }),
                ...(statusFilter    && { status: statusFilter }),
            });
            // 👇 Replace with your actual API endpoint
            const data  = await apiFetchPageOrder(params);
            setOrders(data.data || data.orders || []);
            setTotalPages(data.last_page || data.totalPages || 1);
            setTotalCount(data.total     || data.totalCount || 0);
        } catch (err) {
            console.error("Failed to fetch orders:", err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearch, statusFilter, perPage]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    const handleExport = () => {
        const headers = ["Order ID","Status","Store","Payment","Total","Placed At","Timer"];
        const rows = orders.map(o => [
            o.unique_order_id || o.id,
            STATUS_CONFIG[o.orderstatus_id]?.label || o.orderstatus_id,
            o.restaurant?.name || o.store_name || "",
            o.payment_mode || "", o.sub_total || "",
            o.created_at || "",
        ]);
        const csv  = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href = url; a.download = "orders.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    const handleReset = () => { setSearch(""); setStatusFilter(""); setCurrentPage(1); };

    const pageNums = () => {
        const delta = 2, pages = [];
        for (let i = Math.max(1, currentPage - delta); i <= Math.min(totalPages, currentPage + delta); i++) pages.push(i);
        return pages;
    };
    const nums = pageNums();

    return (
        <div className="order-page">

            {/* Header */}
            <div className="page-header">
                <div>
                    <p className="page-header__eyebrow">Management</p>
                    <h1 className="page-header__title">Order Management</h1>
                </div>
                <div className="page-header__actions">
                    <button className="btn" onClick={handleExport}><ExportIcon /> Export CSV</button>
                    <button className="btn" onClick={handleReset}
                            style={{ color: "#ef4444", borderColor: "#fca5a5", background: "#fef2f2" }}>
                        <ResetIcon /> Reset Filters
                    </button>
                </div>
            </div>

            <div className="card" style={{ padding: 0, overflow: "hidden" }}>

                {/* Toolbar */}
                <div className="om-toolbar">
                    {/* Row 1: search */}
                    <div className="om-search-wrap">
                        <span className="om-search-icon"><SearchIcon /></span>
                        <input className="om-search-input" value={search}
                               onChange={e => setSearch(e.target.value)}
                               placeholder="Search order ID, store, customer..." />
                    </div>

                    {/* Row 2 on mobile: filters + count */}
                    <div className="om-toolbar-filters" style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                        <select className="om-status-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="">All Statuses</option>
                            {Object.entries(STATUS_CONFIG).map(([id, { label }]) => (
                                <option key={id} value={id}>{label}</option>
                            ))}
                        </select>
                        <select
                            className="om-status-select"
                            value={perPage}
                            onChange={e => setPerPage(Number(e.target.value))}
                        >
                            {[10, 15, 25, 50, 100].map(n => (
                                <option key={n} value={n}>{n} / page</option>
                            ))}
                        </select>
                        <span className="om-count-label">
            {loading ? "Loading…" : `${totalCount.toLocaleString()} orders`}
        </span>
                    </div>
                </div>

                {/* Table — desktop/tablet */}
                <div className="om-table-wrap">
                    <table className="om-table">
                        <thead className="om-thead">
                        <tr>
                            <th className="om-th">Order ID</th>
                            <th className="om-th">Status</th>
                            <th className="om-th">Store Name</th>
                            <th className="om-th om-hide-md">Mode</th>
                            <th className="om-th">Total</th>
                            <th className="om-th om-hide-md">Order Placed At</th>
                            <th className="om-th"></th>
                        </tr>
                        </thead>
                        <tbody className="om-tbody">
                        {loading
                            ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                            : orders.length === 0
                                ? (
                                    <tr><td colSpan={8}>
                                        <div className="om-empty">
                                            <div className="om-empty__icon">📋</div>
                                            <div className="om-empty__title">No orders found</div>
                                            <div className="om-empty__sub">Try adjusting your search or filters</div>
                                        </div>
                                    </td></tr>
                                )
                                : orders.map((order, idx) => {
                                    const id = order.unique_order_id || order.id;
                                    return (
                                        <tr key={id || idx}>
                                            <td className="om-td"><span className="om-order-id"># {id?.slice(10)}</span></td>
                                            <td className="om-td"><StatusBadge statusId={order.orderstatus_id} user={order?.user?.name} delivery={order?.accept_delivery?.user?.name} /></td>
                                            <td className="om-td"><span className="om-store-name">{order.restaurant?.name || order.store_name || "—"}</span></td>
                                            <td className="om-td om-hide-md"><span className="om-payment-badge">{order.payment_mode || "—"}</span></td>
                                            <td className="om-td"><span className="om-total">₹{Number(order?.sub_total || 0).toFixed(2)}</span></td>
                                            <td className="om-td om-hide-md"><span className="om-date">{fmtDate(order.created_at)}</span></td>
                                            <td className="om-td">
                                                <button className="om-view-btn" onClick={() => window.open(`/order/${order?.unique_order_id}`, "_blank")}>
                                                    <EyeIcon /> View
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                        </tbody>
                    </table>
                </div>

                {/* Card list — mobile */}
                <div className="om-card-list">
                    {loading
                        ? Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="om-card-row">
                                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                                    <div className="skeleton" style={{ width: 120, height: 14, borderRadius: 6 }} />
                                    <div className="skeleton" style={{ width: 80, height: 20, borderRadius: 20, marginLeft: "auto" }} />
                                </div>
                                <div className="skeleton" style={{ width: "60%", height: 13, borderRadius: 6, marginBottom: 10 }} />
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                    {[80, 60, 110, 90].map((w, j) => (
                                        <div key={j} className="skeleton" style={{ width: w, height: 13, borderRadius: 6 }} />
                                    ))}
                                </div>
                            </div>
                        ))
                        : orders.length === 0
                            ? (
                                <div className="om-empty">
                                    <div className="om-empty__icon">📋</div>
                                    <div className="om-empty__title">No orders found</div>
                                    <div className="om-empty__sub">Try adjusting your search or filters</div>
                                </div>
                            )
                            : orders.map((order, idx) => {
                                const id = order.unique_order_id || order.id;
                                return (
                                    <div key={id || idx} className="om-card-row">
                                        <div className="om-card-row__top">
                                            <span className="om-order-id"># {id?.slice(10)}</span>
                                            <StatusBadge statusId={order.orderstatus_id} user={order?.user?.name} delivery={order?.accept_delivery?.user?.name} />
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 10, fontFamily: "'Inter', sans-serif" }}>
                                            {order.restaurant?.name || order.store_name || "—"}
                                        </div>
                                        <div className="om-card-row__meta">
                                            <div className="om-card-meta-item">
                                                <span className="om-card-meta-label">Total</span>
                                                <span className="om-total" style={{ fontSize: 14 }}>₹{Number(order.sub_total || 0).toFixed(2)}</span>
                                            </div>
                                            <div className="om-card-meta-item">
                                                <span className="om-card-meta-label">Payment</span>
                                                <span className="om-payment-badge">{order.payment_mode || "—"}</span>
                                            </div>
                                            <div className="om-card-meta-item" style={{ gridColumn: "1 / -1" }}>
                                                <span className="om-card-meta-label">Placed At</span>
                                                <span className="om-date">{fmtDate(order.created_at)}</span>
                                            </div>
                                        </div>
                                        <div className="om-card-row__footer">
                                            <button className="om-view-btn" onClick={() => navigate(`/order/${id}`)}>
                                                <EyeIcon /> View
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                    }
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className="om-pagination">
                        <span className="om-pagination__info">Page {currentPage} of {totalPages} · {totalCount.toLocaleString()} orders</span>
                        <div className="om-pagination__pages">
                            <button className="om-page-btn om-page-btn--nav" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                <ChevronLeft />
                            </button>
                            {nums[0] > 1 && (
                                <>{' '}<PageBtn n={1} current={currentPage} onClick={setCurrentPage} />
                                    {nums[0] > 2 && <span className="om-page-ellipsis">…</span>}</>
                            )}
                            {nums.map(n => <PageBtn key={n} n={n} current={currentPage} onClick={setCurrentPage} />)}
                            {nums[nums.length - 1] < totalPages && (
                                <>{nums[nums.length - 1] < totalPages - 1 && <span className="om-page-ellipsis">…</span>}
                                    <PageBtn n={totalPages} current={currentPage} onClick={setCurrentPage} /></>
                            )}
                            <button className="om-page-btn om-page-btn--nav" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                <ChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderManagement;